import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import billingApi, { type Entitlements } from '../services/billingApi';
import { resolveActiveUnitId } from '../utils/activeUnit';

const TTL = 60_000;
const PREFIX = 'vecindus_entitlements:';
type Cached = { value: Entitlements; fetchedAt: number; signature: string };
const memory = new Map<string, Cached>();
const readStored = (key: string): Cached | undefined => { try { const raw = localStorage.getItem(PREFIX + key); if (!raw) return undefined; const parsed = JSON.parse(raw) as Cached; return parsed?.value && parsed?.signature ? parsed : undefined; } catch { return undefined; } };
const writeStored = (key: string, cached: Cached) => { try { localStorage.setItem(PREFIX + key, JSON.stringify(cached)); } catch { /* storage is optional */ } };
const contextKey = (condoId?: string | null, unitId?: string | null) => `${condoId || 'none'}:${unitId || 'none'}`;
const signatureOf = (value: Entitlements) => String((value as any).signature || (value as any).plan_hash || (value as any).updated_at || `${value.tier}:${[...(value.modules || [])].sort().join(',')}`);

export const clearEntitlementsCache = () => { memory.clear(); try { Object.keys(localStorage).filter((key) => key.startsWith(PREFIX)).forEach((key) => localStorage.removeItem(key)); } catch { /* storage is optional */ } };
export const invalidateEntitlementsCache = () => {
  clearEntitlementsCache();
  localStorage.setItem('vecindus_entitlements_invalidated_at', String(Date.now()));
  window.dispatchEvent(new CustomEvent('vecindus:entitlements-invalidated'));
};

// Cache-fix (2026-09-29, Jaime-approved): dedupe in-flight identical requests
// so simultaneous mounts with the same context share one network call.
const inFlightEntitlements = new Map<string, Promise<Entitlements>>();
function fetchEntitlementsOnce(key: string, condoId?: string, unitId?: string): Promise<Entitlements> {
  const existing = inFlightEntitlements.get(key);
  if (existing) return existing;
  const p = billingApi.getEntitlements(condoId, unitId).finally(() => { inFlightEntitlements.delete(key); });
  inFlightEntitlements.set(key, p);
  return p;
}

export function useEntitlements() {
  const { activeCondoId, activeOwnedUnitId, contexts, memberships, user } = useAuth();
  // (2026-09-03, Jaime — home lens is always unit-scoped) AppShell's
  // handleHomeLensChange intentionally deselects condo-linked units when the
  // Hogar lens is active, which used to send getEntitlements() with NO unitId
  // -> backend legacy resolver -> employee-masked menu for condo-linked
  // residents. Resolution is now the SHARED active-unit helper (owners AND
  // residents via the /auth/me contexts payload; standalone AND condo-linked).
  // Condo-lens calls (activeCondoId set) and standalone-unit selection are
  // untouched; the standalone-unit memory and stale-condo purge still win.
  const effectiveUnitId = resolveActiveUnitId({ activeOwnedUnitId, activeCondoId, contexts, memberships });
  const key = contextKey(activeCondoId, effectiveUnitId);
  const stored = readStored(key);
  if (stored) memory.set(key, stored);
  const [value, setValue] = useState<Entitlements | null>(() => memory.get(key)?.value || null);
  // Stale-while-revalidate: `loading` only becomes true when there is NO cached
  // value to render (first-ever load or hard context switch). Background
  // refreshes (TTL expiry, window focus, invalidated event) keep the previous
  // value and entitlementReady=true so the sidebar/menu never flickers.
  const [loading, setLoading] = useState(() => !!user && !memory.has(key));
  const load = useCallback(async (force = false) => {
    if (!user) { setLoading(false); setValue(null); return; }
    const cached = memory.get(key) || readStored(key);
    if (cached) memory.set(key, cached);
    const fresh = !force && cached && Date.now() - cached.fetchedAt < TTL;
    if (fresh) { setValue(cached.value); setLoading(false); return; }
    // Only surface a visible loading state when we have nothing to render.
    // Remember the latest invalidation so an in-flight response that started
    // BEFORE a save (this tab or another) can never repopulate stale limits.
    const invalidatedAt = Number(localStorage.getItem('vecindus_entitlements_invalidated_at') || 0);
    if (!cached) setLoading(true);
    if (cached) setValue(cached.value);
    try {
      const next = await fetchEntitlementsOnce(key, activeCondoId || undefined, effectiveUnitId || undefined);
      if (Number(localStorage.getItem('vecindus_entitlements_invalidated_at') || 0) > invalidatedAt) return; // superseded mid-flight by a newer invalidation
      const cachedNext = { value: next, fetchedAt: Date.now(), signature: signatureOf(next) };
      memory.set(key, cachedNext);
      writeStored(key, cachedNext);
      setValue(next);
    } catch {
      // Keep the last good value on error; never blank the UI.
      if (cached) setValue(cached.value);
    }
    finally { setLoading(false); }
  }, [activeCondoId, effectiveUnitId, key, user]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    // Silent revalidation: keep the cached value in place while refetching so
    // consumers depending on entitlementReady never flip to false.
    const refresh = () => { void load(true); };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    // Cross-tab invalidation: window.dispatchEvent only reaches the same tab,
    // so pricing edits saved in another tab are picked up via the sentinel key
    // the invalidator writes to localStorage (storage events fire in OTHER tabs).
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'vecindus_entitlements_invalidated_at') return;
      memory.clear();
      try { Object.keys(localStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => localStorage.removeItem(k)); } catch { /* storage is optional */ }
      refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    window.addEventListener('vecindus:entitlements-invalidated', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('storage', onStorage); window.removeEventListener('vecindus:entitlements-invalidated', refresh); };
  }, [load]);
  return { entitlement: value, entitlementReady: !loading && !!value, loading, reload: () => load(true) };
}
